import React from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { useThemeClasses, cn } from '../../hooks/useThemeClasses';
import Modal from './Modal';
import Button from './Button';
import SectionHeading from './SectionHeading';
import CornerAccents from './CornerAccents';
import '../../theme-components/03-section-heading.css';

export interface ErrorModalProps {
  isOpen: boolean;
  onClose: () => void;
  errorTitle?: string;
  errorMessage?: string;
  contactPhone?: string;
  contactEmail?: string;
  propertyInfo?: {
    name: string;
    logo?: string;
  };
  showContactSection?: boolean;
  onExit?: () => void;
  fullSize?: boolean;
  showHeader?: boolean;
}

const ErrorModal: React.FC<ErrorModalProps> = ({
  isOpen,
  onClose,
  errorTitle,
  errorMessage,
  contactPhone = "(407) 917-9304",
  contactEmail = "frontdesk@hotel.com",
  propertyInfo,
  showContactSection = true,
  onExit,
  fullSize = false,
  showHeader = true
}) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const tc = useThemeClasses();

  // Use translations as defaults if not provided
  const displayTitle = errorTitle || t('errors.modalTitle');
  const displayMessage = errorMessage || t('errors.modalMessage');

  const handleExit = () => {
    if (onExit) {
      onExit();
    } else {
      onClose();
    }
  };

  // Custom header with hotel branding (only if showHeader is true)
  const customHeader = showHeader && propertyInfo ? (
    <div className="flex items-center gap-3 w-full px-3 py-2">
      <div
        className={cn(
          'w-12 h-12 sharp-corners flex items-center justify-center text-2xl flex-shrink-0',
          tc.bg.surface
        )}
        style={{
          backgroundColor: theme?.brand?.colors?.surface?.base,
          border: `2px solid ${theme?.brand?.colors?.primary}`
        }}
        aria-hidden="true"
      >
        {propertyInfo.logo || '🏨'}
      </div>
      <h1
        className={cn(
          'text-xl sm:text-2xl font-semibold tracking-wide',
          tc.text.primary,
          tc.font.primary
        )}
      >
        {propertyInfo.name}
      </h1>
    </div>
  ) : undefined;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      customHeader={undefined}
      maxWidth="none"
      closeOnOverlayClick={false}
      closeOnEscape={true}
      showCloseButton={false}
      fullSize={true}
      footer={undefined}
    >
      <div className="flex flex-col items-center justify-center text-center p-0 min-h-full">
        <div className="w-full max-w-[560px] px-4">
        {/* Message Card with Corner Accents */}
        <div className={cn('w-full sharp-corners shadow-lg relative mb-9', tc.bg.surface)}
             style={{
               background: 'var(--color-surface-warm)',
               border: '1px solid var(--color-border)',
               boxShadow: 'var(--shadow-card)',
               padding: '40px 44px 36px',
               overflow: 'hidden',
               wordWrap: 'break-word',
               overflowWrap: 'break-word'
             }}>
          <CornerAccents corners="diagonal" />

          {/* Section Heading */}
          <SectionHeading>{t('errors.guestAssistance', 'Guest Assistance')}</SectionHeading>

          {/* Error Title */}
          <h1
            className={cn('text-[22px] font-medium leading-snug mb-4', tc.text.primary)}
            style={{
              fontFamily: 'var(--font-display)',
              color: 'var(--color-primary)',
              lineHeight: '1.35',
              wordWrap: 'break-word',
              overflowWrap: 'break-word'
            }}
            role="alert"
            aria-live="assertive"
          >
            {displayTitle}
          </h1>

          {/* Error Message */}
          <p
            className="text-[15px] leading-relaxed"
            style={{
              fontFamily: 'var(--font-body)',
              color: 'var(--color-text-primary)',
              lineHeight: '1.6',
              fontWeight: 400,
              wordWrap: 'break-word',
              overflowWrap: 'break-word'
            }}
          >
            {displayMessage}
          </p>
        </div>

        {/* Action Buttons */}
        {showContactSection && (
          <div className="flex flex-col gap-3.5 w-full mb-7">
            {/* Call Button */}
            {contactPhone && (
              <a
                href={`tel:${contactPhone.replace(/\D/g, '')}`}
                className="btn-theme-primary flex items-center justify-center gap-2.5 px-8 sharp-corners uppercase transition-all text-white"
                style={{
                  fontFamily: 'var(--font-body)',
                  letterSpacing: '0.08em',
                  fontSize: '14px',
                  fontWeight: 500,
                  padding: '14px 32px',
                  background: 'var(--color-primary)',
                  border: 'none'
                }}
                aria-label={t('errors.callAriaLabel', { phone: contactPhone, defaultValue: `Call ${contactPhone}` })}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
                     style={{ stroke: 'var(--color-gold)', flexShrink: 0 }}>
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
                {t('errors.callGuestRelations', 'Call Guest Relations')}
              </a>
            )}

            {/* Email Button */}
            {contactEmail && (
              <a
                href={`mailto:${contactEmail}?subject=Check-in%20Assistance`}
                className="flex items-center justify-center gap-2.5 px-8 sharp-corners uppercase transition-all"
                style={{
                  fontFamily: 'var(--font-body)',
                  letterSpacing: '0.08em',
                  fontSize: '14px',
                  fontWeight: 500,
                  padding: '14px 32px',
                  background: 'transparent',
                  color: 'var(--color-primary)',
                  border: '1px solid var(--color-primary)'
                }}
                aria-label={t('errors.emailAriaLabel', { email: contactEmail, defaultValue: `Email ${contactEmail}` })}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
                     style={{ stroke: 'var(--color-gold)', flexShrink: 0 }}>
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
                {t('errors.emailGuestRelations', 'Email Guest Relations')}
              </a>
            )}
          </div>
        )}

        {/* Availability Text */}
        <p className="mb-10"
           style={{
             fontFamily: 'var(--font-body)',
             color: 'var(--color-text-secondary)',
             fontSize: '14px',
             fontWeight: 500,
             letterSpacing: '0.08em'
           }}>
          {t('errors.availability247', 'Our team is available 24/7')}
        </p>

        {/* Continue Exploring Button */}
        <div className="w-full">
          <Button
            onClick={handleExit}
            variant="primary"
            size="md"
            className="w-full sharp-corners uppercase"
            style={{
              letterSpacing: '0.08em',
              fontSize: '14px',
              fontWeight: 500,
              padding: '14px 32px'
            }}
            aria-label={t('errors.continueExploring', 'Continue exploring')}
          >
            {t('errors.continueExploring', 'Continue Exploring')}
          </Button>
        </div>
        </div>
      </div>
    </Modal>
  );
};

export default ErrorModal;
